import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaOptionsType } from 'embla-carousel';
import { ContentBlock } from './image-gallery-carousel-block.types';
import AssetWithLink from '@components/asset-with-link/asset-with-link';
import vars from '@variables';
import {
  ChevronLeftAtom,
  ChevronRightAtom,
} from '@atoms/icon-atoms/nav-icon-atoms';
import {
  GalleryCarouselWrapper,
  CarouselWrapper,
  EmblaViewport,
  EmblaContainer,
  EmblaSlide,
  ImageWrapper,
  LeftButton,
  RightButton,
  SlideCount,
  BackgroundColor,
} from './image-gallery-carousel-block.styles';
import { H2Atom, PAtom } from '@atoms/text-atoms/text-atoms';

type ImageGalleryCarouselProps = {
  content: ContentBlock;
};

const ImageGalleryCarousel: React.FC<ImageGalleryCarouselProps> = ({
  content,
}) => {
  const options: EmblaOptionsType = {
    loop: false,
    containScroll: 'keepSnaps',
    dragFree: false,
    align: 'center',
    slidesToScroll: 1,
  };

  const [emblaRef, emblaApi] = useEmblaCarousel(options);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const snaps = emblaApi.scrollSnapList();
    setScrollSnaps(snaps);
    emblaApi.on('select', onSelect);
    onSelect();

    const container = emblaApi.containerNode();

    // Set up ResizeObserver on container
    const observer = new ResizeObserver(() => {
      emblaApi.reInit();
      const newSnaps = emblaApi.scrollSnapList();
      setScrollSnaps(newSnaps);
    });

    if (container) {
      observer.observe(container);
    }

    return () => {
      emblaApi.off('select', onSelect);
      observer.disconnect();
    };
  }, [emblaApi, onSelect]);

  if (!content?.images) return null;

  return (
    <GalleryCarouselWrapper
      isFullWidth={content.isFullWidth}
      enableLightText={content.enableLightText}
    >
      {content.backgroundColor && (
        <BackgroundColor backgroundColor={content.backgroundColor} />
      )}
      <H2Atom>{content?.title}</H2Atom>
      <PAtom>{content?.description}</PAtom>
      <CarouselWrapper key={content.isFullWidth ? 'full' : 'normal'}>
        <LeftButton
          onClick={scrollPrev}
          aria-label="Previous image"
          type="button"
          disabled={selectedIndex === 0}
          isFullWidth={content.isFullWidth}
        >
          <ChevronLeftAtom stroke={vars.emfBlue} />
        </LeftButton>
        <RightButton
          onClick={scrollNext}
          aria-label="Next image"
          type="button"
          disabled={selectedIndex === scrollSnaps.length - 1}
          isFullWidth={content.isFullWidth}
        >
          <ChevronRightAtom stroke={vars.emfBlue} />
        </RightButton>

        <EmblaViewport
          key={content.isFullWidth ? 'full' : 'contained'}
          ref={emblaRef}
          role="region"
          aria-roledescription="carousel"
          aria-label={content?.title || 'Image gallery'}
        >
          <EmblaContainer
            isFullWidth={content.isFullWidth}
            role="group"
            aria-live="polite"
          >
            {content?.images.map((image, index) => (
              <EmblaSlide
                key={image.imageSrc || index}
                isSelected={index === selectedIndex}
                role="group"
                aria-roledescription="slide"
                aria-label={`${index + 1} of ${content.images.length}`}
                backgroundColor={content?.backgroundColor}
              >
                <ImageWrapper>
                  <AssetWithLink
                    description={image.description}
                    alt={
                      image.alt ||
                      image.description ||
                      `Gallery image ${index + 1}`
                    }
                    imageSrc={image.imageSrc}
                    format={image.format}
                    gatsbyImageData={image.gatsbyImageData}
                    linkUrl={image.linkUrl}
                    openInNewTab={image.openInNewTab}
                  />
                </ImageWrapper>
              </EmblaSlide>
            ))}
          </EmblaContainer>
        </EmblaViewport>

        {scrollSnaps.length > 0 && (
          <SlideCount
            enableLightText={content.enableLightText}
            isFullWidth={content.isFullWidth}
            aria-label={`Slide ${selectedIndex + 1} of ${scrollSnaps.length}`}
          >
            {selectedIndex + 1} / {scrollSnaps.length}
          </SlideCount>
        )}
      </CarouselWrapper>
    </GalleryCarouselWrapper>
  );
};

export default ImageGalleryCarousel;
